import { WS_HOSTNAME } from "../constant/host";

/**
 * サーバ → クライアントの共通エンベロープ。
 * WebSocket 仕様書「2. 共通エンベロープ」準拠。
 */
export type Envelope<T = unknown> = {
  type: string;
  payload: T;
};

type CreateSpaSocketOptions = {
  /** "presence" や "events" など、/ws/spa/ 以下のパス名。 */
  path: "presence" | "events";
  /** DRF Token。クエリ `?token=...` で送信する。 */
  token: string;
  /** メッセージ受信時のハンドラ。JSON.parse 済みのエンベロープが渡される。 */
  onMessage: (envelope: Envelope) => void;
  /** `CloseEvent.code === 4401` 受信時に呼ばれる。呼び出し側でログイン導線への遷移を行う想定。 */
  onUnauthorized?: () => void;
  /** 接続確立時（`open` イベント）に呼ばれる。 */
  onOpen?: () => void;
  /**
   * タブが再度 visible になった際の挙動。
   * presence では snapshot を取り直すため再接続が推奨される。
   * events では接続維持のままで良いため、呼び出し側で false にできる。
   * デフォルト: true。
   */
  reconnectOnVisible?: boolean;
};

export type SpaSocket = {
  /** 明示的に切断する。以降自動再接続は行わない。 */
  disconnect: () => void;
  /**
   * 現在の接続に文字列メッセージを送信する。
   * readyState !== OPEN のときは送信せず false を返す（呼び出し側で判断しやすいよう）。
   */
  send: (data: string) => boolean;
};

const MAX_BACKOFF_MS = 30000;
const INITIAL_BACKOFF_MS = 1000;
const UNAUTHORIZED_CODE = 4401;

/**
 * SPA 用 WebSocket クライアント factory。
 * - 認証失敗（close code 4401）時は再接続しない。
 * - それ以外の close は Exponential backoff + ±20% Jitter で再接続。
 * - visibilitychange で visible に戻った際、任意で一度切断して再接続（snapshot 取り直し）。
 */
export const createSpaSocket = ({
  path,
  token,
  onMessage,
  onUnauthorized,
  onOpen,
  reconnectOnVisible = true,
}: CreateSpaSocketOptions): SpaSocket => {
  let ws: WebSocket | null = null;
  let backoff = INITIAL_BACKOFF_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let isDisposed = false;

  const buildUrl = (): string => {
    const url = new URL(`/ws/spa/${path}`, WS_HOSTNAME);
    url.searchParams.set("token", token);
    return url.toString();
  };

  const clearReconnectTimer = () => {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (isDisposed) return;
    const jitter = 1 + (Math.random() * 0.4 - 0.2);
    const delay = Math.min(backoff, MAX_BACKOFF_MS) * jitter;
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    clearReconnectTimer();
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const connect = () => {
    if (isDisposed) return;

    try {
      ws = new WebSocket(buildUrl());
    } catch (err) {
      console.error(`[spaSocket:${path}] failed to instantiate WebSocket`, err);
      scheduleReconnect();
      return;
    }

    ws.addEventListener("open", () => {
      backoff = INITIAL_BACKOFF_MS;
      onOpen?.();
    });

    ws.addEventListener("message", (ev) => {
      try {
        const parsed = JSON.parse(ev.data) as Envelope;
        onMessage(parsed);
      } catch (err) {
        console.warn(`[spaSocket:${path}] failed to parse message`, err);
      }
    });

    ws.addEventListener("close", (ev) => {
      ws = null;
      if (isDisposed) return;
      if (ev.code === UNAUTHORIZED_CODE) {
        onUnauthorized?.();
        return;
      }
      scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // close は別途発火するので、ここではログのみ。
      // 過剰出力を避けるため console.debug を使用。
      console.debug(`[spaSocket:${path}] websocket error`);
    });
  };

  const handleVisibilityChange = () => {
    if (!reconnectOnVisible) return;
    if (document.visibilityState !== "visible") return;
    if (isDisposed) return;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close(1000, "visibility refresh");
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  const disconnect = () => {
    isDisposed = true;
    clearReconnectTimer();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    if (ws) {
      try {
        ws.close(1000, "client disconnect");
      } catch {
        // ignore
      }
      ws = null;
    }
  };

  const send = (data: string): boolean => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      ws.send(data);
      return true;
    } catch (err) {
      console.warn(`[spaSocket:${path}] failed to send`, err);
      return false;
    }
  };

  connect();

  return { disconnect, send };
};
